import camelCase from 'lodash/camelCase';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import React from 'react';
import { Col, Collapse, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import CurrencyFormat from '../../generic/CurrencyFormat';
import DateRange from '../../generic/DateRange';
import { isCreative, isOffsite, isOnsite } from '../../products/utils';
import { clProductLineShape } from '../../prop-types';
import CampaignLineStatusBar from '../CampaignLineStatusBar';
import OnsiteAverageDailyRate from '../OnsiteAverageDailyRate.container';
import CampaignLineHeaderActions from './CampaignLineHeaderActions';
import ConnectedProductLine from './ProductLine.container';

function renderOperativeSelection(op, onChange) {
  const { key, name: opName, value } = op;
  return (
    <Row>
      <Label sm="4" for={`ops-${key}`} className="pr-0">
        Ops {opName}
      </Label>
      <Col sm={8}>
        <Association
          id={`ops-${key}`}
          name={key}
          value={value}
          type="user"
          onChange={onChange}
        />
      </Col>
    </Row>
  );
}

function renderOnsiteAverageDailyRate() {
  return (
    <div className="mt-1">
      <OnsiteAverageDailyRate />
    </div>
  );
}

function renderProductLines(
  expanded,
  products,
  name,
  label,
  onChange,
  compact,
  campaignSave,
) {
  return (
    <Collapse isOpen={expanded}>
      {products.map((product, idx) => (
        <ConnectedProductLine
          {...product}
          name={`${name}.products[${idx}]`}
          business={label}
          onChange={onChange}
          key={product.id}
          compact={compact}
          campaignSave={campaignSave}
        />
      ))}
    </Collapse>
  );
}

function BusinessLine({
  label,
  name,
  products,
  onChange,
  expanded,
  spendableBudget,
  minStartDate,
  maxEndDate,
  pspElement,
  ops,
  spentBudget,
  compact,
  campaignSave,
}) {
  const onsite = isOnsite({ business: label });
  const offsite = isOffsite({ business: label });
  const productLines = renderProductLines(
    expanded,
    products,
    name,
    label,
    onChange,
    compact,
    campaignSave,
  );
  const lineHeader = (
    <CampaignLineHeaderActions
      label={label}
      expanded={expanded}
      products={products}
    />
  );

  const specialContent = ops.map(op => renderOperativeSelection(op, onChange));

  if (onsite) {
    specialContent.push(renderOnsiteAverageDailyRate());
  }

  if (compact) {
    return (
      <>
        <div
          className="c-business-line"
          id={`business-line-${camelCase(label)}`}
        >
          <div className="c-campaign-line">
            <Row className="align-items-center">
              <Col sm={3}>{lineHeader}</Col>
            </Row>
          </div>
        </div>
        {productLines}
      </>
    );
  }

  return (
    <>
      <div className="c-business-line" id={`business-line-${camelCase(label)}`}>
        <div className="c-campaign-line">
          <Row className="align-items-center">
            <Col sm={3}>{lineHeader}</Col>
            <Col sm={2}>
              <div className="c-campaign-line__value">
                <CurrencyFormat
                  name="spendableBudget"
                  value={spendableBudget}
                  text
                />
                <span className="ml-2 small text-muted text-nowrap">
                  <CurrencyFormat name="spentBudget" value={spentBudget} text />{' '}
                  spent
                </span>
              </div>
            </Col>
            <Col sm={2}>
              <div className="c-campaign-line__value">
                <DateRange
                  startName={`${label}.startDate.planned`}
                  endName={`${label}.endDate.planned`}
                  startDate={minStartDate}
                  endDate={maxEndDate}
                  small
                  readOnly
                  text
                />
              </div>
            </Col>
            <Col sm={3}>{specialContent[0]}</Col>
            <Col sm={2}>
              {(onsite || offsite) && (
                <CampaignLineStatusBar business={label} />
              )}
            </Col>
          </Row>
          <Row className="align-items-center">
            <Col sm={3} />
            <Col sm={2}>
              <Label className="c-campaign-line__label">Budget</Label>
            </Col>
            <Col sm={2}>
              <Label className="c-campaign-line__label">Planned runtime</Label>
            </Col>
            <Col sm={3}>
              {specialContent[1]}
              {isCreative({ business: label }) && (
                <Row>
                  <Label sm="4" for="pspElement" className="pr-0">
                    PSP Element
                  </Label>
                  <Col sm={8}>
                    <Input
                      id="cl-pspElement"
                      name="pspElement"
                      value={pspElement}
                      onChange={onChange}
                    />
                  </Col>
                </Row>
              )}
            </Col>
            <Col sm={2}>
              {(onsite || offsite) && (
                <Label className="c-campaign-line__label">Setup status</Label>
              )}
            </Col>
          </Row>
          <Row className="align-items-center mt-1">
            <Col sm={5} />
            <Col sm={2} />
          </Row>
        </div>
      </div>
      {productLines}
    </>
  );
}

BusinessLine.propTypes = {
  label: string.isRequired,
  products: arrayOf(clProductLineShape),
  name: string.isRequired,
  onChange: func,
  expanded: bool,
  spendableBudget: number.isRequired,
  spentBudget: number.isRequired,
  minStartDate: string.isRequired,
  maxEndDate: string.isRequired,
  pspElement: string,
  ops: arrayOf(
    shape({
      name: string.isRequired,
      key: string.isRequired,
      value: number,
    }).isRequired,
  ).isRequired,
  compact: bool.isRequired,
  campaignSave: func.isRequired,
};

BusinessLine.defaultProps = {
  products: [],
  onChange: undefined,
  expanded: false,
  pspElement: '',
};

export default BusinessLine;
